import { useCallback, useContext, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ICompanyList } from "../../../types/common";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CompanyListsTableColumnKeys,
  getCompanyListsTableColumnValue,
} from "./util/common";
import Header from "./Header/Header";
import {
  notification,
  Pagination,
  PaginationProps,
  Skeleton,
  Text,
} from "@findemdev/maui";
import RowCell from "./RowCell/RowCell";
import CreateCustomListsModal from "../CreateCustomListsModal/CreateCustomListsModal";
import {
  useDeleteCompanyListMutation,
} from "../../../../../services/company";
import UserContext from "../../../../../contexts/UserContext";
import DeleteCompanyListModal from "../DeleteCompanyListModal/DeleteCompanyListModal";
import { decodeString } from "@findemdev/findem-helpers";
import { emitGetroEvent } from "../../../utils/emitGetroEvent";
import EmptyTableSVG from "../../../../../assets/images/svgs/EmptyTable.svg";
import { IGetCompanyLogosResponseDataObj } from "../../../../../types/pub";
import {
  TableContainer,
  ScrollableTableContent,
  StyledTable,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  EmptyStateContainer,
  EmptyStateImage,
  EmptyStateText,
  EmptyStateSubtext,
  PaginationContainer,
} from "./Table.styled";

interface ITableProps {
  data: ICompanyList[] | undefined;
  currentPage: number;
  totalPages: number;
  companyDetailsByMetaRef: Record<
    string,
    IGetCompanyLogosResponseDataObj | undefined
  >;
  shortlistedCompanyMetaRefs: string[] | undefined;
  isCompanyDetailsFetching: boolean;
  handleOnPageChange: PaginationProps["onChange"];
  isFetching: boolean;
  isError: boolean;
}

const columnHelper = createColumnHelper<ICompanyList>();

export const Table = ({
  data,
  currentPage,
  totalPages,
  companyDetailsByMetaRef,
  shortlistedCompanyMetaRefs: _shortlistedCompanyMetaRefs,
  isCompanyDetailsFetching: _isCompanyDetailsFetching,
  handleOnPageChange,
  isError: _isError,
  isFetching,
}: ITableProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const userModel = useContext(UserContext);

  const [deleteCompanyList, { isLoading: isDeleteCompanyListLoading }] =
    useDeleteCompanyListMutation();

  const [buildCustomListDetails, setBuildCustomListDetails] = useState<
    ICompanyList | undefined
  >(undefined);
  const [showDeleteCompanyListModal, setShowDeleteCompanyListModal] = useState<
    ICompanyList | undefined
  >(undefined);

  const columns = useMemo(() => {
    const columns = [];

    for (const col of CompanyListsTableColumnKeys) {
      columns.push(
        columnHelper.accessor(
          (row) => {
            return getCompanyListsTableColumnValue({
              key: col,
              data: row,
            });
          },
          {
            id: col,
            size: 300,
            cell: ({ getValue, row, table }) => {
              const value = getValue();

              return (
                <TableCell
                  $isLastColumn={col === "actions"}
                  onClick={() => {
                    if (col === "sharedWithOrg" || col === "actions") {
                      return;
                    }

                    const returnTo = encodeURIComponent(
                      `${location.pathname}${location.search}`
                    );
                    navigate(`company-list/${row.original.ref}?returnTo=${returnTo}`);
                  }}
                >
                  <RowCell
                    col={col}
                    value={value}
                    originalValue={row.original}
                    companyDetailsByMetaRef={companyDetailsByMetaRef}
                    shortlistedCompanyMetaRefs={row.original?.shortlist
                      ?.map((value) => value.meta_ref)
                      .filter((value) => value !== undefined)}
                    handleShowBuildCustomListDetails={
                      table.options.meta?.handleShowBuildCustomListDetails
                    }
                    handleCompanyListDelete={
                      table.options.meta?.handleCompanyListDelete
                    }
                  />
                </TableCell>
              );
            },
            header: () => {
              if (col === "actions") {
                return null;
              }
              return <Header col={col} />;
            },
          }
        )
      );
    }

    return columns;
  }, [data, userModel, companyDetailsByMetaRef, navigate]);

  const handleCreateCustomList = useCallback(async () => {
    setBuildCustomListDetails(undefined);
  }, []);

  const handleCloseCustomListVisible = useCallback(() => {
    setBuildCustomListDetails(undefined);
  }, []);

  const handleCloseCompanyListDeleteModal = useCallback(() => {
    setShowDeleteCompanyListModal(undefined);
  }, []);

  const handleDeleteCompanyList = useCallback(
    async (companyList: ICompanyList) => {
      const name = decodeString(companyList.name);
      try {
        await deleteCompanyList({
          joid: userModel.getUser().uid,
          ref: companyList.ref,
        }).unwrap();

        if (userModel.hasGetroNetwork()) {
          emitGetroEvent('companyBuilder', 'project:deleted', {
            projectRef: companyList.ref,
            name,
            getroListId: companyList.getro_list_id,
          });
        }

        notification.success({
          message: `Successfully delete the company list ${name}`,
        });
      } catch (error) {
        console.error("Error while deleting company list: ", error);
        notification.error({
          message: `Error while deleting company list ${name}`,
        });
      } finally {
        handleCloseCompanyListDeleteModal();
      }
    },
    [handleCloseCompanyListDeleteModal, userModel]
  );

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      handleShowBuildCustomListDetails: (companyList: ICompanyList) => {
        setBuildCustomListDetails(companyList);
      },
      handleCompanyListDelete: async (companyList: ICompanyList) => {
        setShowDeleteCompanyListModal(companyList);
      },
    },
  });

  return (
    <TableContainer>
      <ScrollableTableContent>
        <StyledTable>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeaderCell key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </TableHeader>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id}>
                      <Skeleton
                        loading={isFetching}
                        active={true}
                        paragraph={{
                          rows: 0,
                          width: "100%",
                        }}
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Skeleton>
                    </td>
                  );
                })}
              </TableRow>
            ))}
          </tbody>
        </StyledTable>
        {!table.getRowModel().rows.length &&
          (isFetching ? (
            <Skeleton
              active={true}
              paragraph={{ rows: 2, width: "100%" }}
              style={{ marginTop: 16 }}
            />
          ) : (
            <EmptyStateContainer>
              <div>
                <EmptyStateImage
                  src={EmptyTableSVG as unknown as string}
                  alt="No company lists"
                />
              </div>
              <Text variant="boldData">
                <EmptyStateText>No company lists</EmptyStateText>
              </Text>
              <EmptyStateSubtext>
                You haven&apos;t created any company lists yet, or no lists match your
                filters.
              </EmptyStateSubtext>
            </EmptyStateContainer>
          ))}
      </ScrollableTableContent>

      <PaginationContainer>
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={handleOnPageChange}
          size="small"
          showSizeChanger={false}
          disabled={isFetching}
        />
      </PaginationContainer>
      <CreateCustomListsModal
        data={buildCustomListDetails}
        onOk={handleCreateCustomList}
        onCancel={handleCloseCustomListVisible}
      />
      <DeleteCompanyListModal
        data={showDeleteCompanyListModal}
        isLoading={isDeleteCompanyListLoading}
        onCancel={handleCloseCompanyListDeleteModal}
        onOk={handleDeleteCompanyList}
      />
    </TableContainer>
  );
};

export default Table;
